/**
 * Instants — a real moment on the global timeline, rendered in the **viewer's**
 * timezone (unlike calendar dates and wall-clock times, which are
 * zone-independent and never shifted). On the wire an instant is either Unix
 * **seconds** (a number — timestamps, payment dates) or a **UTC**
 * `YYYY-MM-DD HH:MM:SS` string (accommodation check-in/out). HTML
 * `datetime-local` inputs are in the viewer's local zone.
 *
 * Display deliberately uses the runtime timezone — that *is* the viewer's zone.
 * The test suite pins `TZ=UTC`, so the assertions are deterministic.
 */

export type InstantStyle =
	| 'datetime' // 'Jan 15, 2:30 PM'
	| 'datetimeWithYear' // 'Jan 15, 2026, 2:30 PM'
	| 'dateWeekday' // 'Thu, Jan 15, 2026'
	| 'dateMedium' // 'Jan 15, 2026'
	| 'dateLong' // 'January 15, 2026'
	| 'time' // '2:30 PM'

const STYLE_OPTS: Record<InstantStyle, Intl.DateTimeFormatOptions> = {
	datetime: { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true },
	datetimeWithYear: {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	},
	dateWeekday: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
	dateMedium: { year: 'numeric', month: 'short', day: 'numeric' },
	dateLong: { year: 'numeric', month: 'long', day: 'numeric' },
	time: { hour: 'numeric', minute: '2-digit', hour12: true },
}

const pad = (n: number): string => String(n).padStart(2, '0')

/** Current time as Unix seconds. */
export const nowUnix = (): number => Math.floor(Date.now() / 1000)

/** Unix seconds → the JS `Date` for that instant. */
export const unixToDate = (unix: number): Date => new Date(unix * 1000)

/** Parse a UTC `YYYY-MM-DD HH:MM:SS` wire string into the instant it denotes. */
export const parseUtcInstant = (utcString: string): Date => new Date(utcString.replace(' ', 'T') + 'Z')

/** Format a real instant in the viewer's timezone. */
export const formatInstant = (date: Date, style: InstantStyle = 'dateMedium'): string =>
	date.toLocaleString('en-US', STYLE_OPTS[style])

/** Format Unix seconds in the viewer's timezone; `null` for `null`/`undefined` input. */
export const formatUnix = (
	unix: number | null | undefined,
	style: InstantStyle = 'dateMedium',
): string | null => (unix == null ? null : formatInstant(unixToDate(unix), style))

/** Format a UTC wire string in the viewer's timezone. */
export const formatUtcInstant = (utcString: string, style: InstantStyle = 'dateMedium'): string =>
	formatInstant(parseUtcInstant(utcString), style)

/** A JS `Date` → datetime-local input `YYYY-MM-DDTHH:MM` (viewer-local fields). */
export const dateToInput = (date: Date): string =>
	`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`

/**
 * Unix seconds → ISO calendar date `YYYY-MM-DD` in the viewer's zone — the day
 * the viewer sees the instant fall on, matching how the app renders it.
 * For machine-friendly artifacts (CSV exports); use `formatUnix` for prose.
 */
export const formatUnixIsoDate = (unix: number): string => {
	const date = unixToDate(unix)
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** datetime-local input (viewer-local) → UTC wire string `YYYY-MM-DD HH:MM:SS`. */
export const inputToUtcString = (localInput: string): string => {
	const date = new Date(localInput)
	return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
}

/** UTC wire string → datetime-local input (viewer-local) for editing. */
export const utcStringToInput = (utcString: string): string => dateToInput(parseUtcInstant(utcString))
